import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useMemo, useCallback } from 'react';
import { pathComparator } from '@truqc/content-path';
// eslint-disable-next-line import/no-named-as-default-member
import saveFormBehavior from '../../../../nativeBehaviors/saveFormBehavior';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import useGenerateChecks from './useGenerateChecks';

/**
 * Custom hook for checks settings modal state and handlers.
 * Extracts logic from ChecksSettingsModal to reduce component size.
 */
function useChecksSettings(_ref) {
  let _ref$checksCollection = _ref.checksCollection,
    checksCollection = _ref$checksCollection === void 0 ? {} : _ref$checksCollection,
    _ref$defaultChecks = _ref.defaultChecks,
    defaultChecks = _ref$defaultChecks === void 0 ? {} : _ref$defaultChecks,
    path = _ref.path,
    formActions = _ref.formActions,
    reduxEntity = _ref.reduxEntity,
    _ref$content = _ref.content,
    content = _ref$content === void 0 ? null : _ref$content,
    notificationActions = _ref.notificationActions,
    _ref$pendingOperation = _ref.pendingOperation,
    pendingOperation = _ref$pendingOperation === void 0 ? false : _ref$pendingOperation,
    onClose = _ref.onClose;
  const _useState = useState('list'),
    _useState2 = _slicedToArray(_useState, 2),
    currentView = _useState2[0],
    setCurrentView = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    userRequest = _useState4[0],
    setUserRequest = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isGenerating = _useState6[0],
    setIsGenerating = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    suggestions = _useState8[0],
    setSuggestions = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    addingId = _useState0[0],
    setAddingId = _useState0[1];
  const mergedChecks = useMemo(() => {
    const merged = _objectSpread({}, checksCollection);
    Object.entries(defaultChecks || {}).forEach(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 2),
        id = _ref3[0],
        defaultCheck = _ref3[1];
      if (!merged[id]) {
        merged[id] = _objectSpread(_objectSpread({}, defaultCheck), {}, {
          enabled: false,
          isDefault: true
        });
      }
    });
    return merged;
  }, [checksCollection, defaultChecks]);
  const checksArray = useMemo(() => {
    const arr = Object.entries(mergedChecks || {}).map(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 2),
        id = _ref5[0],
        check = _ref5[1];
      return _objectSpread({
        id
      }, check);
    });
    return arr.sort(pathComparator('_created'));
  }, [mergedChecks]);
  const handleGenerate = useGenerateChecks({
    checksArray,
    content,
    setSuggestions,
    setIsGenerating,
    notificationActions,
    pendingOperation
  });
  const handleToggle = useCallback(async (checkId, newEnabled) => {
    try {
      const state = store.getState();
      const active = state[reduxEntity].active;
      if (newEnabled && defaultChecks[checkId] && !checksCollection[checkId]) {
        const defaultCheck = defaultChecks[checkId];
        await formActions.change(active.id, "content".concat(path, "/settings/checks/").concat(checkId), _objectSpread(_objectSpread({}, defaultCheck), {}, {
          enabled: true,
          isDefault: true
        }));
      } else {
        await formActions.change(active.id, "content".concat(path, "/settings/checks/").concat(checkId, "/enabled"), newEnabled);
      }
    } catch (error) {
      logger('Error toggling check:', error);
      notificationActions.info({
        message: 'Failed to update check setting. Please try again.'
      }, {
        autoClose: 5000
      });
    }
  }, [defaultChecks, checksCollection, formActions, path, reduxEntity, notificationActions]);
  const handleDelete = useCallback(async checkId => {
    try {
      const state = store.getState();
      const active = state[reduxEntity].active;
      if (checksCollection[checkId]) {
        await formActions.change(active.id, "content".concat(path, "/settings/checks/").concat(checkId), null);
      }
    } catch (error) {
      logger('Error deleting check:', error);
      notificationActions.info({
        message: 'Failed to delete check. Please try again.'
      }, {
        autoClose: 5000
      });
    }
  }, [checksCollection, formActions, path, reduxEntity, notificationActions]);
  const handleGenerateClick = useCallback(async () => {
    await handleGenerate(userRequest);
  }, [handleGenerate, userRequest]);
  const handleAddSuggestion = useCallback(async suggestion => {
    setAddingId(suggestion.id);
    try {
      const state = store.getState();
      const active = state[reduxEntity].active;
      const newCheckId = suggestion.id || "custom_".concat(Date.now());
      const newCheck = {
        query: suggestion.query,
        description: suggestion.description,
        category: suggestion.category || 'general',
        severity: suggestion.severity || 'medium',
        isDefault: false,
        enabled: true,
        _created: Date.now()
      };
      await formActions.change(active.id, "content".concat(path, "/settings/checks/").concat(newCheckId), newCheck);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (error) {
      logger('Error adding suggestion:', error);
      notificationActions.info({
        message: 'Failed to add check. Please try again.'
      }, {
        autoClose: 5000
      });
    } finally {
      setAddingId(null);
    }
  }, [formActions, path, reduxEntity, notificationActions]);
  const handleClose = useCallback(async () => {
    try {
      // eslint-disable-next-line import/no-named-as-default-member
      await saveFormBehavior.evaluate({}, reduxEntity);
    } catch (error) {
      logger('Error saving form on modal close:', error);
      notificationActions.info({
        message: 'Unable to save changes. Please try again.'
      }, {
        autoClose: 5000
      });
    } finally {
      setCurrentView('list');
      setUserRequest('');
      setSuggestions([]);
      onClose();
    }
  }, [reduxEntity, onClose, notificationActions]);
  const handleNavigateToGenerate = useCallback(() => {
    setCurrentView('generate');
  }, []);
  const handleNavigateToList = useCallback(() => {
    setCurrentView('list');
  }, []);
  return {
    currentView,
    checksArray,
    userRequest,
    setUserRequest,
    isGenerating,
    suggestions,
    addingId,
    handleToggle,
    handleDelete,
    handleGenerateClick,
    handleAddSuggestion,
    handleClose,
    handleNavigateToGenerate,
    handleNavigateToList
  };
}
export default useChecksSettings;